/* eslint-disable */

import React, { useEffect, useMemo, useCallback } from 'react'
import { api } from '../requests/axios'
import { Box, Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { useState } from 'react'
import { ColorResult } from 'react-color'
import { useNavigate, useSearchParams } from "react-router-dom"
import { useColorChart } from '../hooks/useColorChart'
import ColorChartCanvas, { CellDimensions } from './ColorChartCanvas'
import ColorChartControls from './ColorChartControls'
import ColorChartHeader from './ColorChartHeader'
import { getCellDimensions } from '../utils/cellDimensions'

interface IntarsiaCanvasProps {
  chartId?: number;
  initialData?: string;
  initialName?: string;
  initialDescription?: string | null;
  initialWidth?: number;
  initialHeight?: number;
  initialCraft?: 'knitting' | 'crochet' | 'cross_stitch';
  initialCrochetStitchType?: 'sc' | 'hdc' | 'dc';
  onSave?: () => void;
}

export default function IntarsiaCanvas(props: IntarsiaCanvasProps = {}) {
  const {
    chartId,
    initialData,
    initialName,
    initialDescription,
    initialWidth = 10,
    initialHeight = 10,
    initialCraft,
    initialCrochetStitchType,
    onSave
  } = props;
  const [name, setName] = useState<string | null>(initialName || null);
  const [description, setDescription] = useState<string | null>(initialDescription || null);
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEditMode = !!chartId;
  
  // Get craft and crochet_stitch_type from props (edit mode) or URL params (new mode)
  const craftFromUrl = searchParams.get('craft') as 'knitting' | 'crochet' | 'cross_stitch' | null
  const crochetStitchTypeFromUrl = searchParams.get('crochet_stitch_type') as 'sc' | 'hdc' | 'dc' | null
  
  // State for craft and stitch type (editable in edit mode)
  const [craft, setCraft] = useState<'knitting' | 'crochet' | 'cross_stitch' | null>(
    isEditMode && initialCraft ? initialCraft : craftFromUrl
  )
  const [crochetStitchType, setCrochetStitchType] = useState<'sc' | 'hdc' | 'dc' | null>(
    isEditMode && initialCrochetStitchType ? initialCrochetStitchType : crochetStitchTypeFromUrl
  )
  
  // Handle craft change
  const handleCraftChange = useCallback((newCraft: 'knitting' | 'crochet' | 'cross_stitch') => {
    setCraft(newCraft)
    // Clear crochet stitch type if switching away from crochet
    if (newCraft !== 'crochet') {
      setCrochetStitchType(null)
    }
  }, [])
  
  // Handle crochet stitch type change
  const handleCrochetStitchTypeChange = useCallback((newStitchType: 'sc' | 'hdc' | 'dc') => {
    setCrochetStitchType(newStitchType)
  }, [])
  
  // Get cell dimensions based on craft and stitch type
  const cellDimensions = useMemo(() => {
    return getCellDimensions(craft, crochetStitchType)
  }, [craft, crochetStitchType])
  
  // Validate required fields for new charts
  useEffect(() => {
    if (!isEditMode) {
      if (!craft) {
        navigate('/color-charts')
        return
      }
      if (craft === 'crochet' && !crochetStitchType) {
        navigate('/color-charts')
        return
      }
    }
  }, [craft, crochetStitchType, isEditMode, navigate])

  // Memoize parsed data to avoid re-parsing on every render
  const parsedData = useMemo(() => {
    if (!initialData) return undefined;
    try {
      const parsed = JSON.parse(initialData);
      // Handle both old format (just colors) and new format (colors + text)
      if (parsed.colors) {
        return parsed.colors;
      }
      return parsed;
    } catch {
      return undefined;
    }
  }, [initialData]);

  // Parse text data separately
  const parsedTextData = useMemo(() => {
    if (!initialData) return undefined;
    try {
      const parsed = JSON.parse(initialData);
      return parsed.text || {};
    } catch {
      return undefined;
    }
  }, [initialData]);

  const {
    data,
    width,
    height,
    color,
    brushSize,
    zoom,
    tool,
    textData,
    textInput,
    fontStyle,
    textAlignment,
    canvasRef,
    canUndo,
    canRedo,
    setWidth,
    setHeight,
    setColor,
    setBrushSize,
    setZoom,
    setTool,
    setTextInput,
    setFontStyle,
    setTextAlignment,
    undo,
    redo,
    getColorAt,
    getTextPreviewCells,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    fitToView,
    reset: resetChart
  } = useColorChart({
    initialWidth: initialWidth || 10,
    initialHeight: initialHeight || 10,
    initialData: parsedData,
    initialTextData: parsedTextData,
    cellDimensions: cellDimensions
  })

  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const sendData = useCallback(async (): Promise<void> => {
    if (!craft) {
      alert('Craft is required')
      return
    }
    
    const chartData: any = { 
      name: name,
      description: description,
      data: JSON.stringify(data),
      height: height,
      width: width,
      craft: craft
    }
    
    // Include crochet_stitch_type if craft is crochet
    if (craft === 'crochet') {
      if (!crochetStitchType) {
        alert('Crochet stitch type is required')
        return
      }
      chartData.crochet_stitch_type = crochetStitchType
    }
    
    let body = {
      color_chart: chartData
    }
    
    try {
      let response;
      if (isEditMode && chartId) {
        response = await api.put(
          process.env.REACT_APP_API_PROXY_URL?.concat(`/color_charts/${chartId}.json`) || '',
          body
        )
      } else {
        response = await api.post(
          process.env.REACT_APP_API_PROXY_URL?.concat(`/color_charts.json`) || '',
          body
        )
      }
      
      const responseData = await response.data
      console.log(responseData)

      if(response.status === 200) {
        alert('Amazing!')
        if (onSave) {
          onSave();
        } else {
          navigate("/color-charts")
        }
      }
    } catch (error: any) {
      const errors = error.response?.data?.errors || ['An error occurred while saving the color chart']
      setErrorMessages(Array.isArray(errors) ? errors : [errors])
      setErrorModalOpen(true)
    }
  }, [name, description, data, height, width, isEditMode, chartId, craft, crochetStitchType, onSave, navigate]);

  const handleColorChange = useCallback((colorResult: ColorResult): void => {
    setColor(colorResult.hex)
  }, [setColor]);

  const handleReset = useCallback((): void => {
    resetChart();
    setName(null);
    setDescription(null);
  }, [resetChart]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && ((e.shiftKey && e.key === 'Z') || e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        setZoom(prev => Math.min(1, prev + 0.1));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setZoom(prev => Math.max(0.1, prev - 0.1));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        setZoom(1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [undo, redo, setZoom])

  // Get header text for edit mode
  const getHeaderText = useCallback(() => {
    if (!isEditMode || !craft) return null;
    
    const craftLabels: Record<string, string> = {
      knitting: 'Knitting',
      crochet: 'Crochet',
      cross_stitch: 'Cross Stitch'
    }
    
    const stitchLabels: Record<string, string> = {
      sc: 'Single Crochet',
      hdc: 'Half Double Crochet',
      dc: 'Double Crochet'
    }
    
    if (craft === 'crochet' && crochetStitchType) {
      return `Edit ${stitchLabels[crochetStitchType]} Chart`
    }
    
    return `Edit ${craftLabels[craft]} Chart`
  }, [isEditMode, craft, crochetStitchType])

  const headerText = getHeaderText()

  const [craftModalOpen, setCraftModalOpen] = useState(false)
  const [stitchModalOpen, setStitchModalOpen] = useState(false)

  const handleOpenCraftModal = () => {
    setCraftModalOpen(true)
  }

  const handleCloseCraftModal = () => {
    setCraftModalOpen(false)
  }

  const handleCraftSelect = (selectedCraft: 'knitting' | 'crochet' | 'cross_stitch') => {
    handleCraftChange(selectedCraft)
    setCraftModalOpen(false)
    if (selectedCraft === 'crochet') {
      setStitchModalOpen(true)
    }
  }

  const handleOpenStitchModal = () => {
    if (craft === 'crochet') {
      setStitchModalOpen(true)
    }
  }

  const handleCloseStitchModal = () => {
    setStitchModalOpen(false)
  }

  const handleStitchSelect = (selectedStitch: 'sc' | 'hdc' | 'dc') => {
    handleCrochetStitchTypeChange(selectedStitch)
    setStitchModalOpen(false)
  }

  const getCraftButtonLabel = () => {
    if (!craft) return 'Craft'
    
    const craftLabels: Record<string, string> = {
      knitting: 'Knitting',
      crochet: 'Crochet',
      cross_stitch: 'Cross Stitch'
    }
    
    const stitchLabels: Record<string, string> = {
      sc: 'Single Crochet',
      hdc: 'Half Double',
      dc: 'Double'
    }
    
    if (craft === 'crochet' && crochetStitchType) {
      return stitchLabels[crochetStitchType]
    }
    
    return craftLabels[craft] || 'Craft'
  }

  return (
    <Box sx={{ padding: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {isEditMode && headerText && (
        <Box sx={{ mb: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {headerText}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenCraftModal}
            sx={{ px: 1.5, py: 0.5, fontSize: '0.875rem' }}
          >
            {getCraftButtonLabel()}
          </Button>
        </Box>
      )}
      <ColorChartHeader
        name={name}
        description={description}
        canUndo={canUndo}
        canRedo={canRedo}
        tool={tool}
        textInput={textInput}
        brushSize={brushSize}
        fontStyle={fontStyle}
        textAlignment={textAlignment}
        craft={craft}
        crochetStitchType={crochetStitchType}
        isEditMode={isEditMode}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onUndo={undo}
        onRedo={redo}
        onSave={sendData}
        onReset={handleReset}
        onToolChange={setTool}
        onTextInputChange={setTextInput}
        onBrushSizeChange={setBrushSize}
        onFontStyleChange={setFontStyle}
        onTextAlignmentChange={setTextAlignment}
        onCraftChange={handleCraftChange}
        onCrochetStitchTypeChange={handleCrochetStitchTypeChange}
        showCraftButton={false}
      />

      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ColorChartControls
            width={width}
            height={height}
            zoom={zoom}
            color={color}
            onWidthChange={setWidth}
            onHeightChange={setHeight}
            onZoomChange={setZoom}
            onColorChange={handleColorChange}
            onFitToView={fitToView}
          />
        </Grid>

        <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ColorChartCanvas
            width={width}
            height={height}
            data={data}
            zoom={zoom}
            tool={tool}
            cellDimensions={cellDimensions}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseUp={handleMouseUp}
            getColorAt={getColorAt}
            textInput={textInput}
            getTextPreviewCells={getTextPreviewCells}
            color={color}
            canvasRef={canvasRef}
          />
        </Grid>
      </Grid>

      {/* Craft Selection Modal */}
      {craftModalOpen && (
        <Dialog
          open={craftModalOpen}
          onClose={handleCloseCraftModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Craft</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Button
                variant={craft === 'knitting' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('knitting')}
                sx={{ py: 2 }}
              >
                Knitting
              </Button>
              <Button
                variant={craft === 'crochet' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('crochet')}
                sx={{ py: 2 }}
              >
                Crochet
              </Button>
              <Button
                variant={craft === 'cross_stitch' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleCraftSelect('cross_stitch')}
                sx={{ py: 2 }}
              >
                Cross Stitch
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCraftModal}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Crochet Stitch Type Modal */}
      {stitchModalOpen && (
        <Dialog
          open={stitchModalOpen}
          onClose={handleCloseStitchModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Choose Stitch Type</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Button
                variant={crochetStitchType === 'sc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('sc')}
                sx={{ py: 2 }}
              >
                Single Crochet
              </Button>
              <Button
                variant={crochetStitchType === 'hdc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('hdc')}
                sx={{ py: 2 }}
              >
                Half Double Crochet
              </Button>
              <Button
                variant={crochetStitchType === 'dc' ? 'contained' : 'outlined'}
                fullWidth
                size="large"
                onClick={() => handleStitchSelect('dc')}
                sx={{ py: 2 }}
              >
                Double Crochet
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStitchModal}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Error Saving Color Chart</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {errorMessages.map((error, index) => (
              <Typography key={index} sx={{ mb: 1, color: 'error.main' }}>
                {error}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

